import {
    Box,
    Stack,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { useList, useSelect, useTranslate } from "@refinedev/core";
import { CreateOrEdit } from "@/components/editcreate";
import { TextFieldElement } from "react-hook-form-mui";
import { RHFAutocomplete } from "@/components/form/RHFAutocomplete";
import { zodResolver } from "@hookform/resolvers/zod";
import { editCollectionSchema, createCollectionSchema, FormValuesType } from "@/utils/zod/productCollectionFormValidation";
import RHFAutoCompleteString from "@/components/form/RHFAutocompleteString";
import { IDeal } from "@/utils/zod/dealFormValidation";
import { uomsData } from "@/utils/data-provider/data";
import { AddMediaForm } from "@/components/form/AddMediaForm";
import { IMedia } from "@/interfaces";

interface Props {
    actionType: "create" | "edit";
    redirect?: boolean;
    onSave?: (finishValues: FormValuesType<"edit", "collections">) => void;
}

export const CollectionAddEditForm = ({ actionType, redirect = true, onSave }: Props) => {

    const { data: dealOptions } = useList<IDeal>({
        resource: "deals",
    });
    const formValues = useForm<FormValuesType<typeof actionType, "collections">>({
        refineCoreProps: {
            resource: "collections",
            redirect: redirect ? "list" : false,
            onMutationSuccess: (data: any) => {
                if (actionType === "create" && onSave && data?.data) {
                    onSave(data.data as FormValuesType<"edit", "collections">);
                }
            },
            ...(actionType === "edit" && {
                queryOptions: {
                    select: (data: any) => {
                        const products = data.data.products?.map((product: { id: string }) => product.id) || [];
                        const transformedData = ({
                            ...data.data,
                            products: products,
                        });
                        return { data: transformedData };
                    }
                },
            }),
        },
        resolver: zodResolver(actionType === "create" ? createCollectionSchema : editCollectionSchema
        ),
        ...(actionType === "create" && {
            defaultValues: {
                name: "",
                description: null,
                minimum_order_quantity: null,
                uom: null,
                cost_price: null,
                tags: [],
                medias: [],
                products: [],
                status: "active",
                deal_name: "",
            } as FormValuesType<typeof actionType, "collections">,
        }),
    });

    // Set deal_name if it's available and form is in create mode
    if (actionType === "create" && dealOptions?.data?.length && !formValues.getValues("deal_name")) {
        formValues.setValue("deal_name", dealOptions.data[0].name);
    }

    const { register, handleSubmit, refineCore: { onFinish } } = formValues;
    const t = useTranslate();
    if (actionType === "edit") {
        register("id");
    }

    const { options: ProductOptions } = useSelect({
        resource: "products",
        optionLabel: "name",
        optionValue: "id",
    });

    // modal for collection update
    // const { show, visible, close } = useModal();

    const onSubmit = handleSubmit((values) => {
        const finishValues = {
            ...values,
            minimum_order_quantity: values.minimum_order_quantity || 1,
            uom: values.uom || "pcs",
            products: values.products.map((id: string) => ({ id })),
            medias: values.medias.map((media: IMedia) => ({ id: media.id }))
        }
        onFinish(finishValues);
    });
    
    return (
        <>
            <CreateOrEdit actionType={actionType} formValues={formValues} handleSubmit={onSubmit} title={{create: t("collections.titles.create"), edit: t("collections.titles.edit")}}>
                <Box sx={{ p: 3 }}>
                    <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2 }} autoComplete="off">
                        <AddMediaForm name="medias" allowMultiple={false} />
                        <Stack direction="row" gap={2}>
                            <TextFieldElement
                                name="name"
                                label={t("collections.fields.name")}
                                fullWidth
                                autoFocus
                                required
                                autoComplete="no-name"
                                slotProps={{
                                    inputLabel: {
                                        sx: {
                                            '& .MuiFormLabel-asterisk': {
                                                color: 'error.main',
                                                fontSize: '24px',
                                            },
                                        },
                                    },
                                }}
                                size="small"
                                sx={{ flex: 0.5 }}
                            />

                            <RHFAutocomplete
                                name={"products"}
                                label={t("collections.fields.product")}
                                options={ProductOptions}
                                multiple={true}
                                size="small"
                                sx={{ flex: 1 }}
                            />
                        </Stack>

                        <TextFieldElement
                            name="description"
                            label={t("products.fields.description")}
                            multiline
                            fullWidth
                            autoComplete="no-name"
                            size="small"
                        />

                        <Stack direction="row" gap={2}>
                            <TextFieldElement
                                label={t("products.fields.price")}
                                name={'cost_price'}
                                type={'number'}
                                fullWidth
                                autoComplete="no-name"
                                size="small"
                                sx={{ flex: 1 }}
                            />

                            <TextFieldElement
                                name="minimum_order_quantity"
                                label={t("products.fields.moq")}
                                type="number"
                                fullWidth
                                autoComplete="no-name"
                                size="small"
                                sx={{ flex: 1 }}
                            />

                            <RHFAutoCompleteString
                                name="uom"
                                label={t("products.fields.uom")}
                                options={uomsData}
                                size="small"
                                createNew={true}
                                sx={{ flex: 1 }}
                            />
                        </Stack>

                        <RHFAutoCompleteString
                            name="deal_name"
                            label={t("products.fields.deal_name")}
                            options={dealOptions?.data?.map((deal) => deal.name) || []}
                            required
                            size="small"
                        />
                    </Box>
                </Box>

                {/* 
              <CollectionUpdateModal
                  open={visible}
                  onCancel={close}
                  onClose={() => {
                      const values = getValues();
                      const finishValues = {
                          ...values,
                          products: values.products.map((id: number) => ({ id: id.toString() }))
                      };
                      onFinish(finishValues);
                  }}
              /> 
          */}

            </CreateOrEdit>
        </>
    );
};
